import { MemberProfile } from "../../../../model/members/MemberProfile";

// Which parts of a member's profile can be outstanding. A member who signed up as
// a guest — or abandoned onboarding part-way — typically has one or more of these
// still to do, and until now had to hunt around the profile tabs to find them.
// The section list drives both the top-of-profile banner and the wizard steps.
export type CompletionSectionKey = "personal" | "directDebit" | "health";

export interface CompletionSection {
    key: CompletionSectionKey;
    // Wizard step / banner label.
    title: string;
    // Member-facing one-liner explaining what's outstanding.
    description: string;
    // For "personal", the individual field labels still to fill (so the banner can
    // spell them out). The other sections are all-or-nothing and leave this empty.
    missingItems: string[];
}

// The personal details a member must supply for a complete profile. This is the
// SAME required set the self-service create ("Create User" / onboarding) screen
// gates its Save on — PersonalDetailsForm imports it from here so the two can
// never drift apart.
export interface RequiredPersonalField {
    // Matches both the MemberProfile property and the PersonalDetailsForm field name.
    name: "name" | "phoneNumber" | "dateOfBirth" | "addressLine1" | "townCity" | "postZipCode";
    label: string;
}

export const REQUIRED_PERSONAL_FIELDS: readonly RequiredPersonalField[] = [
    { name: "name", label: "Full name" },
    { name: "phoneNumber", label: "Phone number" },
    { name: "dateOfBirth", label: "Date of birth" },
    { name: "addressLine1", label: "Address line 1" },
    { name: "townCity", label: "Town / City" },
    { name: "postZipCode", label: "Postcode" },
];

// The API's Member.DateOfBirth is a non-nullable DateTime, so a member created
// without one arrives as a sentinel (DateTime.MinValue → 0001-01-01, or a legacy
// 1900-01-01 import) rather than null. Anything before this instant is "not
// provided" rather than a real birth date. Compared as an absolute timestamp
// against a UTC boundary, NOT via getFullYear() — a local-year comparison is
// timezone-sensitive right at a year boundary (in Europe/London, 1901-01-01Z is
// still 1900 locally, thanks to the pre-1847 LMT offset).
const MIN_PLAUSIBLE_DOB_MS = Date.UTC(1901, 0, 1);

// What a create flow sends when it never asked for a date of birth: the
// DateTime.MinValue sentinel the API stores and the Python importers write. Sent
// EXPLICITLY rather than omitting the key, so the payload says what it means
// instead of relying on the server's default for a missing field.
//
// Date-only, like every other calendar field on the request (validFrom/validTo).
// A birth date has no time, the column is `date`, and the timestamp spelling drags
// timezone conversion into a value that can't survive it — "0001-01-01T00:00:00Z"
// would underflow DateTime.MinValue if anything ever shifted it to local time.
export const DATE_OF_BIRTH_NOT_PROVIDED = "0001-01-01";

// Normalise the shapes a date-of-birth reaches us in: a Date (revived by
// DateReviver on the API response), a dayjs instance (the live form value in
// PersonalDetailsForm), or a raw ISO string.
const toDate = (value: unknown): Date | null => {
    if (value instanceof Date) return value;
    if (typeof value === "string") return value.trim() ? new Date(value) : null;
    if (typeof value === "object" && value !== null && typeof (value as { toDate?: unknown }).toDate === "function") {
        return (value as { toDate: () => Date }).toDate();
    }
    return null;
};

export function hasRealDateOfBirth(value: unknown): boolean {
    const date = toDate(value);
    if (!date || Number.isNaN(date.getTime())) return false;
    return date.getTime() >= MIN_PLAUSIBLE_DOB_MS;
}

// Text a sign-up flow or import wrote to satisfy a NOT NULL column, standing in for
// content nobody has supplied. Guest signup used to fill the whole address with
// "To be Completed", and legacy imports do the same, so treating it as an answer
// told members their address was done and hid it from the completion prompt.
// Shared with the mandate check below — the two must agree on what a placeholder is.
const PLACEHOLDER_TEXT = ["to be completed", "guest member"];

export function isPlaceholderText(value: string): boolean {
    const trimmed = value.trim();
    // All-zero strings are the numeric form of the same thing: the /newmember Quick
    // Save writes phoneNumber "00000000000", and mandates carry 00000000 / 000000.
    // "004321" is real data — only an entirely-zero string is a placeholder.
    if (trimmed.length > 0 && /^0+$/.test(trimmed)) return true;
    return PLACEHOLDER_TEXT.includes(trimmed.toLowerCase());
}

// Whether a required personal field holds real member-supplied content. Strings
// are trimmed (whitespace isn't an answer) and placeholder text doesn't count;
// date of birth gets the sentinel guard.
export function isPersonalFieldFilled(name: RequiredPersonalField["name"], value: unknown): boolean {
    if (name === "dateOfBirth") return hasRealDateOfBirth(value);
    if (typeof value === "string") return value.trim().length > 0 && !isPlaceholderText(value);
    return value != null;
}

// An emergency contact is complete once it names someone AND gives a way to reach
// them — the same bar NewMemberCustomForm applies (name required, plus a phone
// unless an email was given). PersonalDetailsForm writes the sub-object with null
// members when the fields are blank, so a present-but-empty object is not an answer.
//
// Deliberately NOT part of REQUIRED_PERSONAL_FIELDS: that set gates the onboarding
// Save button, and the self-service flow already loses members at that gate. This is
// surfaced in the completion prompt so it gets asked for, without becoming a third
// hard blocker on the way in.
export function hasEmergencyContact(member: MemberProfile): boolean {
    const contact = member.metadata?.EmergencyContact;
    if (!contact) return false;
    const named = typeof contact.name === "string" && contact.name.trim().length > 0
        && !isPlaceholderText(contact.name);
    const reachable = [contact.phoneNumber, contact.emailAddress].some(
        (v) => typeof v === "string" && v.trim().length > 0,
    );
    return named && reachable;
}

// The labels of the required personal details still outstanding. `read` pulls the
// current value for a field name, so this serves both a loaded MemberProfile and
// a live form (which holds dayjs dates and un-saved edits).
export function missingPersonalItems(
    read: (name: RequiredPersonalField["name"]) => unknown,
    options: { includeName?: boolean } = {},
): string[] {
    const { includeName = true } = options;
    return REQUIRED_PERSONAL_FIELDS.filter((field) => {
        if (field.name === "name" && !includeName) return false;
        return !isPersonalFieldFilled(field.name, read(field.name));
    }).map((field) => field.label);
}

// Direct debit only applies to plans that are actually collected by direct debit.
// Annual and one-off memberships are paid up front; `None` / `Unknown` are the
// not-billed frequencies (guest and placeholder categories). A member on any of
// those isn't "incomplete" for having no mandate, and nagging a £0 guest for bank
// details would be plain wrong. `paymentFrequency` is a bare string on the model,
// so this compares loosely rather than exhaustively over a union.
const NON_DIRECT_DEBIT_FREQUENCIES = ["Annual", "OneTime", "None", "Unknown"];

export function requiresDirectDebit(member: MemberProfile): boolean {
    const category = member.membershipCategory;
    // A hidden £0 category is the "Default New Member" placeholder the server
    // assigns to any sign-up who didn't pick a plan (V44 seeds it as price 0 /
    // OneTime / is_visible false). That means "plan not assigned yet", NOT
    // "fee-free" — and it's exactly the cohort this prompt exists for, so it must
    // fall through to the billed default rather than the £0 exemption below.
    //
    // Guests are deliberately included. Someone who signed up at the desk and then
    // logs in is doing so to join, and must get the SAME onboarding as a member who
    // signed up online: personal details, direct debit, health. A guest who never
    // intends to join simply doesn't log in.
    const isUnassignedPlaceholder = !!category && category.isVisible === false && category.amount === 0;
    if (!isUnassignedPlaceholder) {
        // Nothing to collect — a genuine fee-free category never needs a mandate.
        if (category && category.amount === 0) return false;
        const frequency = category?.paymentFrequency;
        if (frequency) return !NON_DIRECT_DEBIT_FREQUENCIES.includes(frequency);
    }
    // Unknown / unset / placeholder plan ⇒ treated as billed. Same conservative
    // default the self-service create flow uses (it can't see the plan either, so
    // it asks for a mandate unless the member opts out).
    return true;
}

// The four fields that make up a direct-debit mandate, as collected by
// DirectDebitInput while no mandate exists yet.
export interface DirectDebitMandateInput {
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    sortCode: string;
}

// A stored mandate field that doesn't actually hold bank details. Legacy imports
// and part-completed sign-ups leave placeholder text (see PLACEHOLDER_TEXT) or
// zero-filled numbers behind, so those count as "not on file". DirectDebitInput
// applies the same rule to decide whether a member may still edit their mandate —
// it imports this so the two can't drift. Keep the placeholder list in step with
// DirectDebitMandateRules.Placeholders on the server.
export function isMandateFieldEmpty(value: string | undefined): boolean {
    if (!value) return true;
    const trimmed = value.trim();
    if (trimmed === "") return true;
    return isPlaceholderText(trimmed);
}

// Whether a real mandate is already on file (all four fields hold genuine data).
// Used for display decisions — see isMandateSelfEditable for the "may the member
// still submit one?" question, which is deliberately stricter.
export function isMandateOnFile(mandate: DirectDebitMandateInput | null | undefined): boolean {
    if (!mandate) return false;
    return (
        !isMandateFieldEmpty(mandate.accountHolderName) &&
        !isMandateFieldEmpty(mandate.bankName) &&
        !isMandateFieldEmpty(mandate.accountNumber) &&
        !isMandateFieldEmpty(mandate.sortCode)
    );
}

// Whether the member may still submit their own mandate. This MIRRORS the server's
// gate on POST /members/me/directdebit/update (DirectDebitMandateRules.IsEmpty),
// which allows a self-update whenever the stored ACCOUNT NUMBER or SORT CODE holds no
// collectable value — missing, "To be Completed", or all zeros. Keep the two in step:
// if this says yes and the server says no, completion offers a Direct Debit step whose
// save can only ever 400.
//
// Keying on the bank numbers (rather than requiring all four fields to be blank)
// is what makes a partial mandate visible. A guest signup used to store
// 'Guest Member' next to 00000000/000000, and legacy imports leave the same shape:
// the non-empty account holder name made it look settled, so the member got no
// prompt to complete their details and no way to add them. Completion asks this
// rather than `isMandateOnFile` because the question is "can they still fix it?".
export function isMandateSelfEditable(mandate: DirectDebitMandateInput | null | undefined): boolean {
    if (!mandate) return true; // nothing stored at all
    return isMandateFieldEmpty(mandate.accountNumber) || isMandateFieldEmpty(mandate.sortCode);
}

// A fully-valid direct-debit mandate (matches the DD form's own rules: non-empty
// account holder + bank, 8-digit account number, 6-digit sort code). Used to
// decide whether a mandate can be submitted and to gate the Save/Complete action.
export function isDirectDebitComplete(dd: DirectDebitMandateInput | null | undefined): boolean {
    return !!(
        dd?.accountHolderName?.trim() &&
        dd?.bankName?.trim() &&
        /^\d{8}$/.test(dd?.accountNumber ?? "") &&
        /^\d{6}$/.test(dd?.sortCode ?? "")
    );
}

export interface CompletionInputs {
    member: MemberProfile;
    // Whether the member may edit their own name (mode.editName). Self-service
    // locks it, so a blank name can't be fixed from here — it's dropped from the
    // outstanding list rather than shown as an un-actionable item the member can
    // never clear.
    canEditName: boolean;
    // Whether a usable direct-debit mandate is settled: `true` = one is on file (or
    // the member can no longer submit one themselves — see isMandateSelfEditable),
    // `false` = nothing stored and the member may still enter one, `null` = not
    // established yet (still loading, or the fetch failed), in which case the
    // section is omitted rather than reported as missing off incomplete data.
    directDebitComplete: boolean | null;
    // A populated health questionnaire submission exists. `null` = not known yet.
    healthComplete: boolean | null;
    // Whether the health questionnaire applies to this view at all (mode.health).
    healthApplies: boolean;
}

// The outstanding sections, in the order the wizard walks them. An empty result
// means the profile is complete — the banner hides and the wizard has nothing to
// collect.
export function computeProfileCompletion(inputs: CompletionInputs): CompletionSection[] {
    const { member, canEditName, directDebitComplete, healthComplete, healthApplies } = inputs;
    const sections: CompletionSection[] = [];

    const personalMissing = missingPersonalItems(
        (name) => (member as unknown as Record<string, unknown>)[name],
        { includeName: canEditName },
    );
    // Prompted alongside the required fields — it lives in the same form, so the
    // wizard's personal step already lands the member on it — but it does not gate
    // Save (see hasEmergencyContact).
    if (!hasEmergencyContact(member)) personalMissing.push("Emergency contact");
    if (personalMissing.length > 0) {
        sections.push({
            key: "personal",
            title: "Personal details",
            description: "We still need some of your contact and address details.",
            missingItems: personalMissing,
        });
    }

    if (directDebitComplete === false && requiresDirectDebit(member)) {
        sections.push({
            key: "directDebit",
            title: "Direct Debit",
            description: "Your membership is billed by direct debit — set up your mandate.",
            missingItems: [],
        });
    }

    if (healthApplies && healthComplete === false) {
        sections.push({
            key: "health",
            title: "Health questionnaire",
            description: "Complete your health questionnaire so the club can look after you safely.",
            missingItems: [],
        });
    }

    return sections;
}
